import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

import { CtaButton } from "@/components/CtaButton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
// Loaded on demand: keeps the Supabase client (~200KB parsed) out of the entry chunk.
const getSupabase = async () => (await import("@/integrations/supabase/client")).supabase;
import { getAttribution } from "@/lib/attribution";
import { NAP, SHOW_PHONE } from "@/lib/business";
import { notifyLead } from "@/lib/notify-lead";
import { cn } from "@/lib/utils";

/*
 * TURNSTILE INTEGRATION POINT
 * ---------------------------
 * Cloudflare Turnstile is not wired up: there is no site key yet, and a broken
 * CAPTCHA silently blocks every lead. When a key exists, render the widget just
 * above the submit button, hold the token in state, and refuse submission until
 * it resolves. Verify the token server-side before the insert.
 *
 * Turnstile MUST be added before running paid traffic — the honeypot and the
 * client-side throttle below stop casual bots, not a targeted spam run.
 */

export type LeadFormType = "contact" | "booking_request" | "scorecard";

export interface LeadFormProps {
  formType: LeadFormType;
  heading?: string;
  interestOptions: readonly string[];
  submitLabel: string;
  onSuccessRedirect: string;
  /** Adds the "When suits you?" textarea, written to leads.preferred_times. */
  showPreferredTimes?: boolean;
  className?: string;
}

interface Values {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  interest: string;
  message: string;
  preferred_times: string;
  consent_marketing: boolean;
}

const EMPTY: Values = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company: "",
  interest: "",
  message: "",
  preferred_times: "",
  consent_marketing: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const THROTTLE_MS = 20_000;
const THROTTLE_KEY = "laa_last_lead_submit";

function validate(field: keyof Values, values: Values): string | null {
  if (field === "email") {
    const value = values.email.trim();
    if (!value) return "Enter your email address so we can reply.";
    if (!EMAIL_RE.test(value)) return "That email address does not look right.";
    if (value.length > 255) return "That email address is too long.";
  }
  if (field === "first_name" && values.first_name.length > 100) {
    return "Keep your first name under 100 characters.";
  }
  if (field === "last_name" && values.last_name.length > 100) {
    return "Keep your last name under 100 characters.";
  }
  if (field === "company" && values.company.length > 200) {
    return "Keep the company name under 200 characters.";
  }
  if (field === "message" && values.message.length > 2000) {
    return "Keep the message under 2000 characters.";
  }
  if (field === "preferred_times" && values.preferred_times.length > 500) {
    return "Keep the times under 500 characters.";
  }
  return null;
}

const fieldClass =
  "mt-2 h-[52px] rounded-lg border border-ink-200 bg-paper-0 px-4 text-[16px] text-ink-900 placeholder:text-ink-400 transition-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50";
const labelClass = "text-body-sm font-medium text-ink-900";

export function LeadForm({
  formType,
  heading,
  interestOptions,
  submitLabel,
  onSuccessRedirect,
  showPreferredTimes = false,
  className,
}: LeadFormProps) {
  const navigate = useNavigate();
  const id = React.useId();
  const [values, setValues] = React.useState<Values>(EMPTY);
  const [errors, setErrors] = React.useState<Partial<Record<keyof Values, string>>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);
  const honeypot = React.useRef<HTMLInputElement>(null);

  const set = <K extends keyof Values>(key: K, value: Values[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleBlur = (field: keyof Values) => () => {
    const error = validate(field, values);
    setErrors((prev) => ({ ...prev, [field]: error ?? undefined }));
  };

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    // Honeypot: a real person never sees this field. Pretend everything worked.
    if (honeypot.current?.value) {
      void navigate({ to: onSuccessRedirect });
      return;
    }

    const nextErrors: Partial<Record<keyof Values, string>> = {};
    for (const field of Object.keys(values) as (keyof Values)[]) {
      const error = validate(field, values);
      if (error) nextErrors[field] = error;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      document.getElementById(`${id}-email`)?.focus();
      return;
    }

    // Throttle: one submission per browser per 20 seconds.
    try {
      const last = Number(window.localStorage.getItem(THROTTLE_KEY) ?? 0);
      if (last && Date.now() - last < THROTTLE_MS) {
        setFormError("You just sent a message. Give it a moment before sending another.");
        return;
      }
    } catch {
      /* storage blocked — skip the throttle */
    }

    setSubmitting(true);
    const leadId =
      typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : undefined;

    try {
      const attribution = getAttribution();
      const { error } = await (await getSupabase()).from("leads").insert({
        ...(leadId ? { id: leadId } : {}),
        form_type: formType,
        first_name: values.first_name.trim() || null,
        last_name: values.last_name.trim() || null,
        email: values.email.trim(),
        phone: values.phone.trim() || null,
        company: values.company.trim() || null,
        interest: values.interest || null,
        message: values.message.trim() || null,
        preferred_times: values.preferred_times.trim() || null,
        consent_marketing: values.consent_marketing,
        ...attribution,
      });

      if (error) throw error;

      try {
        window.localStorage.setItem(THROTTLE_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }

      // Fire-and-forget alert. Failures are swallowed inside notifyLead and
      // must never block the redirect or surface to the visitor.
      void notifyLead(leadId);

      void navigate({ to: onSuccessRedirect, state: { leadId } as never });
    } catch (error) {
      console.error("Lead submission failed", error);
      setFormError(
        SHOW_PHONE
          ? `We could not send that. Please try again, or call us on ${NAP.phone} and we will take the details over the phone.`
          : `We could not send that. Please try again, or email us at ${NAP.email} and we will take the details that way.`,
      );
    } finally {
      setSubmitting(false);
    }
  }

  const errorFor = (field: keyof Values, fieldId: string) =>
    errors[field] ? (
      <p id={`${fieldId}-error`} className="mt-2 text-body-sm text-danger">
        <span aria-hidden="true">! </span>
        {errors[field]}
      </p>
    ) : null;

  return (
    <form onSubmit={handleSubmit} noValidate className={cn("w-full", className)}>
      {heading ? <h2 className="font-display text-h3 text-ink-900">{heading}</h2> : null}

      <div aria-live="polite" className="sr-only">
        {Object.values(errors).filter(Boolean).join(" ")}
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${id}-first`} className={labelClass}>
            First name
          </label>
          <Input
            id={`${id}-first`}
            name="first_name"
            autoComplete="given-name"
            className={fieldClass}
            value={values.first_name}
            onChange={(e) => set("first_name", e.target.value)}
            onBlur={handleBlur("first_name")}
            aria-invalid={errors.first_name ? true : undefined}
            aria-describedby={errors.first_name ? `${id}-first-error` : undefined}
          />
          {errorFor("first_name", `${id}-first`)}
        </div>

        <div>
          <label htmlFor={`${id}-last`} className={labelClass}>
            Last name
          </label>
          <Input
            id={`${id}-last`}
            name="last_name"
            autoComplete="family-name"
            className={fieldClass}
            value={values.last_name}
            onChange={(e) => set("last_name", e.target.value)}
            onBlur={handleBlur("last_name")}
            aria-invalid={errors.last_name ? true : undefined}
            aria-describedby={errors.last_name ? `${id}-last-error` : undefined}
          />
          {errorFor("last_name", `${id}-last`)}
        </div>

        <div>
          <label htmlFor={`${id}-email`} className={labelClass}>
            Email <span className="text-brand-700">(required)</span>
          </label>
          <Input
            id={`${id}-email`}
            name="email"
            type="email"
            required
            autoComplete="email"
            className={fieldClass}
            value={values.email}
            onChange={(e) => set("email", e.target.value)}
            onBlur={handleBlur("email")}
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={errors.email ? `${id}-email-error` : undefined}
          />
          {errorFor("email", `${id}-email`)}
        </div>

        <div>
          <label htmlFor={`${id}-phone`} className={labelClass}>
            Phone
          </label>
          <Input
            id={`${id}-phone`}
            name="phone"
            type="tel"
            autoComplete="tel"
            className={fieldClass}
            value={values.phone}
            onChange={(e) => set("phone", e.target.value)}
            onBlur={handleBlur("phone")}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor={`${id}-company`} className={labelClass}>
            Company
          </label>
          <Input
            id={`${id}-company`}
            name="company"
            autoComplete="organization"
            className={fieldClass}
            value={values.company}
            onChange={(e) => set("company", e.target.value)}
            onBlur={handleBlur("company")}
            aria-invalid={errors.company ? true : undefined}
            aria-describedby={errors.company ? `${id}-company-error` : undefined}
          />
          {errorFor("company", `${id}-company`)}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor={`${id}-interest`} className={labelClass}>
            What can we help with?
          </label>
          <select
            id={`${id}-interest`}
            name="interest"
            className="mt-2 flex h-[52px] w-full rounded-lg border border-ink-200 bg-paper-0 px-4 text-[16px] text-ink-900 transition-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50"
            value={values.interest}
            onChange={(e) => set("interest", e.target.value)}
          >
            <option value="">Select an option</option>
            {interestOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor={`${id}-message`} className={labelClass}>
            Message
          </label>
          <Textarea
            id={`${id}-message`}
            name="message"
            rows={5}
            className="mt-2 rounded-lg border-ink-200 bg-paper-0 text-[16px] text-ink-900 placeholder:text-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50"
            value={values.message}
            onChange={(e) => set("message", e.target.value)}
            onBlur={handleBlur("message")}
            aria-invalid={errors.message ? true : undefined}
            aria-describedby={errors.message ? `${id}-message-error` : undefined}
          />
          {errorFor("message", `${id}-message`)}
        </div>

        {showPreferredTimes ? (
          <div className="sm:col-span-2">
            <label htmlFor={`${id}-times`} className={labelClass}>
              When suits you? (rough days and times are fine)
            </label>
            <Textarea
              id={`${id}-times`}
              name="preferred_times"
              rows={3}
              className="mt-2 rounded-lg border-ink-200 bg-paper-0 text-[16px] text-ink-900 placeholder:text-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50"
              value={values.preferred_times}
              onChange={(e) => set("preferred_times", e.target.value)}
              onBlur={handleBlur("preferred_times")}
              aria-invalid={errors.preferred_times ? true : undefined}
              aria-describedby={errors.preferred_times ? `${id}-times-error` : undefined}
            />
            {errorFor("preferred_times", `${id}-times`)}
          </div>
        ) : null}
      </div>

      {/* Honeypot — hidden from people and assistive tech, catnip for bots. */}
      <div className="sr-only" aria-hidden="true">
        <label htmlFor={`${id}-website`}>Do not fill this in</label>
        <input
          ref={honeypot}
          id={`${id}-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="mt-6 flex items-start gap-3">
        <Checkbox
          id={`${id}-consent`}
          checked={values.consent_marketing}
          onCheckedChange={(checked) => set("consent_marketing", checked === true)}
          className="mt-1"
        />
        <label htmlFor={`${id}-consent`} className="text-body-sm text-ink-600">
          I agree to be contacted about my enquiry. See our{" "}
          <a
            href="/legal/privacy"
            className="text-brand-700 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
          >
            Privacy Policy
          </a>
          .
        </label>
      </div>

      <div aria-live="polite">
        {formError ? (
          <p className="mt-6 rounded-lg border border-danger/40 bg-danger/5 p-4 text-body-sm text-danger">
            <span aria-hidden="true">! </span>
            {formError}{" "}
            {SHOW_PHONE ? (
              <a href={NAP.phoneHref} className="underline underline-offset-4">
                {NAP.phone}
              </a>
            ) : null}
          </p>
        ) : null}
      </div>

      <CtaButton type="submit" size="lg" className="mt-8 w-full sm:w-auto" disabled={submitting}>
        {submitting ? (
          <>
            <Loader2 size={20} strokeWidth={1.5} className="animate-spin" aria-hidden="true" />
            Sending
          </>
        ) : (
          submitLabel
        )}
      </CtaButton>
    </form>
  );
}

export default LeadForm;
